/**
 * CopilotWidget — floating companion rendered in a portal on document.body.
 *
 *  - Minimized: round floating bubble (bottom-right) with pending badge.
 *  - Open: 380x560 window, draggable by its title bar (hand-rolled pointer
 *    events, no lib), messages, proposed-action cards (Confirmer/Rejeter),
 *    collapsible execution timeline, input (Enter to send) and a
 *    backend-availability indicator. Dark slate styling, works on any page.
 */

import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent as ReactChangeEvent,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { useCopilotStore } from '@/stores/copilot-store';
import { useAdvancedMode } from '@/components/AdvancedMode';

import { getCurrent } from './context-registry';
import { uploadCopilotFile } from './file-upload';
import type { CopilotAction, CopilotActionType, CopilotMessage, ExecutionStatus } from './types';

const ACTION_LABELS: Record<CopilotActionType, string> = {
  navigate: 'Navigation',
  open_form: 'Ouvrir un formulaire',
  fill_field: 'Remplir un champ',
  fill_form: 'Remplir un formulaire',
  submit_form: 'Soumettre un formulaire',
  select_table_rows: 'Sélectionner des lignes',
  highlight: 'Mettre en évidence',
  open_modal: 'Ouvrir une fenêtre',
  scroll_to: 'Défiler vers',
  toast: 'Notification',
  import_records: 'Importer des enregistrements',
  wait_user_confirm: 'Attendre une confirmation',
  execute_tool: 'Exécuter une action',
};

const STATUS_STYLES: Record<ExecutionStatus, { dot: string; label: string }> = {
  executed: { dot: 'bg-emerald-400', label: 'exécutée' },
  failed: { dot: 'bg-rose-400', label: 'échouée' },
  skipped: { dot: 'bg-slate-400', label: 'ignorée' },
};

function clampPos(p: { x: number; y: number }): { x: number; y: number } {
  if (typeof window === 'undefined') return p;
  return {
    x: Math.min(Math.max(8, p.x), Math.max(8, window.innerWidth - 100)),
    y: Math.min(Math.max(8, p.y), Math.max(8, window.innerHeight - 56)),
  };
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '';
  }
}

function summarizeTarget(action: CopilotAction): string {
  const t = action.target;
  if (!t) return '';
  const parts: string[] = [];
  if (t.tool) parts.push(`outil : ${t.tool}`);
  if (t.route) parts.push(`route : ${t.route}`);
  if (t.page_id) parts.push(`page : ${t.page_id}`);
  if (t.form_id) parts.push(`formulaire : ${t.form_id}`);
  if (t.field) parts.push(`champ : ${t.field}`);
  return parts.join(' · ');
}

function MessageBubble({ message }: { message: CopilotMessage }) {
  if (message.role === 'system') {
    return <p className="text-center text-[11px] italic text-slate-500">{message.content}</p>;
  }
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] whitespace-pre-wrap break-words rounded-lg px-2.5 py-1.5 text-sm ${
          isUser ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-100'
        }`}
      >
        {message.content}
        {message.actions && message.actions.length > 0 && (
          <p className={`mt-1 text-[10px] ${isUser ? 'text-indigo-200' : 'text-slate-400'}`}>
            {message.actions.length} action(s) proposée(s)
          </p>
        )}
      </div>
    </div>
  );
}

function ActionCard({
  action,
  onConfirm,
  onReject,
}: {
  action: CopilotAction;
  onConfirm: () => void;
  onReject: () => void;
}) {
  const label = ACTION_LABELS[action.type] ?? action.type;
  const target = summarizeTarget(action);
  return (
    <div className="rounded-lg border border-slate-600 bg-slate-800 p-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-slate-100">{label}</span>
        {action.preview && (
          <span className="rounded border border-amber-500/50 px-1 text-[10px] text-amber-300">
            aperçu
          </span>
        )}
      </div>
      {target && <p className="mt-0.5 truncate text-[11px] text-slate-400">{target}</p>}
      <div className="mt-1.5 flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          aria-label={`Confirmer l’action : ${label}`}
          className="rounded bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-500"
        >
          {action.requires_confirmation ? 'Confirmer' : 'Exécuter'}
        </button>
        <button
          type="button"
          onClick={onReject}
          aria-label={`Rejeter l’action : ${label}`}
          className="rounded border border-slate-600 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700"
        >
          Rejeter
        </button>
      </div>
    </div>
  );
}

export function CopilotWidget() {
  const {
    open,
    minimized,
    position,
    mode,
    messages,
    pendingActions,
    timeline,
    toasts,
    backendAvailable,
    sending,
    model,
    temperature,
    maxSteps,
    openWidget,
    setMinimized,
    setPosition,
    setMode,
    setModel,
    setTemperature,
    setMaxSteps,
    sendMessage,
    confirmAction,
    rejectAction,
    dismissToast,
  } = useCopilotStore();

  const { advanced: copilotAdvanced, toggle: toggleCopilotAdvanced } = useAdvancedMode('copilot');

  const [draft, setDraft] = useState('');
  const [showTimeline, setShowTimeline] = useState(false);
  const [livePos, setLivePos] = useState<{ x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);
  const lastDragPosRef = useRef<{ x: number; y: number } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pos = clampPos(livePos ?? position);
  const canAttach = backendAvailable && !sending;

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, sending]);

  const handleSend = () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft('');
    void sendMessage(text, getCurrent());
  };

  const onFileChange = (e: ReactChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (file) void uploadCopilotFile(file);
  };

  const onDragOver = (e: ReactDragEvent<HTMLDivElement>) => {
    if (!canAttach) return;
    if (Array.from(e.dataTransfer.types).includes('Files')) {
      e.preventDefault();
      setDragging(true);
    }
  };

  const onDragLeave = (e: ReactDragEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDragging(false);
  };

  const onDrop = (e: ReactDragEvent<HTMLDivElement>) => {
    setDragging(false);
    if (!canAttach) return;
    const file = e.dataTransfer.files?.[0];
    if (file) {
      e.preventDefault();
      void uploadCopilotFile(file);
    }
  };

  const onTitlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Do not start a drag from the title-bar buttons.
    if ((e.target as HTMLElement).closest('button')) return;
    dragOffsetRef.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onTitlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const offset = dragOffsetRef.current;
    if (!offset) return;
    const next = clampPos({ x: e.clientX - offset.dx, y: e.clientY - offset.dy });
    lastDragPosRef.current = next;
    setLivePos(next);
  };

  const onTitlePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragOffsetRef.current) return;
    dragOffsetRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // pointer capture already released
    }
    if (lastDragPosRef.current) {
      setPosition(lastDragPosRef.current);
      lastDragPosRef.current = null;
    }
    setLivePos(null);
  };

  const showWindow = open && !minimized;

  const bubble = (
    <button
      type="button"
      onClick={openWidget}
      aria-label="Ouvrir le copilot"
      className="fixed bottom-5 right-5 z-[10001] flex h-14 w-14 items-center justify-center rounded-full bg-indigo-600 text-white shadow-xl transition-colors hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8 10h8m-8 4h5m-9 6 2.6-2.6A2 2 0 0 1 8 17h9a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7a3 3 0 0 0-3 3v13Z"
        />
      </svg>
      {pendingActions.length > 0 && (
        <span
          className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-slate-900"
          aria-label={`${pendingActions.length} action(s) en attente`}
        >
          {pendingActions.length}
        </span>
      )}
      {!backendAvailable && (
        <span
          className="absolute -left-0.5 -top-0.5 h-3 w-3 rounded-full border border-slate-900 bg-amber-400"
          title="Backend copilot indisponible"
          aria-hidden="true"
        />
      )}
    </button>
  );

  const panel = (
    <div
      role="dialog"
      aria-label="Copilot"
      className="fixed z-[10001] flex h-[560px] max-h-[calc(100vh-16px)] w-[380px] max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-xl border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl"
      style={{ left: pos.x, top: pos.y }}
    >
      {/* Title bar — drag handle */}
      <div
        className="flex h-11 shrink-0 cursor-move touch-none select-none items-center gap-2 border-b border-slate-700 bg-slate-800 px-3"
        onPointerDown={onTitlePointerDown}
        onPointerMove={onTitlePointerMove}
        onPointerUp={onTitlePointerUp}
        onPointerCancel={onTitlePointerUp}
      >
        <span
          className={`h-2.5 w-2.5 shrink-0 rounded-full ${
            backendAvailable ? 'bg-emerald-400' : 'bg-amber-400'
          }`}
          title={backendAvailable ? 'Backend copilot connecté' : 'Backend copilot indisponible'}
          aria-hidden="true"
        />
        <span className="text-sm font-semibold">Copilot</span>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            mode === 'agent' ? 'bg-amber-500/20 text-amber-300' : 'bg-sky-500/20 text-sky-300'
          }`}
        >
          {mode === 'agent' ? 'Agent' : 'Assistant'}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            role="switch"
            aria-checked={copilotAdvanced}
            onClick={toggleCopilotAdvanced}
            aria-label="Mode avancé"
            title={copilotAdvanced ? 'Masquer les options avancées' : 'Afficher les options avancées'}
            className={`rounded px-2 py-1 text-[11px] ${
              copilotAdvanced ? 'bg-indigo-500/20 text-indigo-200' : 'text-slate-300 hover:bg-slate-700'
            }`}
          >
            Avancé
          </button>
          <button
            type="button"
            onClick={() => setMode(mode === 'agent' ? 'assistant' : 'agent')}
            aria-label={
              mode === 'agent' ? 'Passer en mode assistant' : 'Passer en mode agent'
            }
            className="rounded px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-700"
          >
            {mode === 'agent' ? 'Mode assistant' : 'Mode agent'}
          </button>
          <button
            type="button"
            onClick={() => setMinimized(true)}
            aria-label="Réduire le copilot"
            className="rounded px-2 py-1 text-slate-300 hover:bg-slate-700"
          >
            <span aria-hidden="true">—</span>
          </button>
        </div>
      </div>

      {/* Backend availability banner */}
      {!backendAvailable && (
        <div
          role="status"
          className="shrink-0 border-b border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-300"
        >
          Backend copilot indisponible — mode local, actions simulées marquées « aperçu ».
        </div>
      )}

      {/* Advanced options (model / temperature / agent steps) */}
      {copilotAdvanced && (
        <div className="shrink-0 space-y-2 border-b border-slate-700 bg-slate-800/60 px-3 py-2 text-xs text-slate-300">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Options avancées
          </p>
          <label className="flex items-center justify-between gap-2">
            <span>Modèle</span>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              aria-label="Modèle du copilot"
              className="rounded border border-slate-600 bg-slate-900 px-1.5 py-1 text-xs text-slate-100"
            >
              <option value="auto">Auto</option>
              <option value="fast">Rapide</option>
              <option value="balanced">Équilibré</option>
              <option value="quality">Qualité</option>
            </select>
          </label>
          <label className="flex items-center justify-between gap-2">
            <span>Température ({temperature.toFixed(1)})</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.1}
              value={temperature}
              onChange={(e) => setTemperature(Number(e.target.value))}
              aria-label="Température"
              className="w-28 accent-indigo-500"
            />
          </label>
          {mode === 'agent' && (
            <label className="flex items-center justify-between gap-2">
              <span>Étapes max (agent)</span>
              <input
                type="number"
                min={1}
                max={10}
                value={maxSteps}
                onChange={(e) => setMaxSteps(Number(e.target.value))}
                aria-label="Nombre maximal d’étapes de l’agent"
                className="w-16 rounded border border-slate-600 bg-slate-900 px-1.5 py-1 text-xs text-slate-100"
              />
            </label>
          )}
          <p className="text-[10px] text-slate-500">
            Envoyées au copilot ; ignorées tant que le backend ne les exploite pas.
          </p>
        </div>
      )}

      {/* Messages */}
      <div
        ref={listRef}
        role="log"
        aria-live="polite"
        aria-label="Messages du copilot"
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`relative flex-1 space-y-2 overflow-y-auto px-3 py-2 ${
          dragging ? 'ring-2 ring-inset ring-indigo-500' : ''
        }`}
      >
        {messages.length === 0 && (
          <p className="pt-8 text-center text-xs text-slate-500">
            Posez une question, déposez un fichier ou demandez une action au copilot.
          </p>
        )}
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        {sending && <p className="text-xs text-slate-400">Le copilot réfléchit…</p>}
        {dragging && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-slate-900/80 text-sm font-medium text-indigo-300">
            Déposez le fichier pour l’analyser
          </div>
        )}
      </div>

      {/* Proposed actions */}
      {pendingActions.length > 0 && (
        <div className="max-h-44 shrink-0 space-y-2 overflow-y-auto border-t border-slate-700 bg-slate-800/60 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Actions proposées
          </p>
          {pendingActions.map((action) => (
            <ActionCard
              key={action.action_id}
              action={action}
              onConfirm={() => confirmAction(action.action_id)}
              onReject={() => rejectAction(action.action_id)}
            />
          ))}
        </div>
      )}

      {/* Execution timeline (collapsible) */}
      <div className="shrink-0 border-t border-slate-700">
        <button
          type="button"
          onClick={() => setShowTimeline((v) => !v)}
          aria-expanded={showTimeline}
          aria-label="Afficher ou masquer la timeline des actions exécutées"
          className="flex w-full items-center justify-between px-3 py-1.5 text-xs text-slate-400 hover:bg-slate-800"
        >
          <span>Timeline des actions ({timeline.length})</span>
          <span aria-hidden="true">{showTimeline ? '▾' : '▸'}</span>
        </button>
        {showTimeline && (
          <ul className="max-h-32 overflow-y-auto px-3 pb-2 text-xs" aria-label="Actions exécutées">
            {timeline.length === 0 && (
              <li className="text-slate-500">Aucune action exécutée pour l’instant.</li>
            )}
            {[...timeline].reverse().map((entry, index) => {
              const style = STATUS_STYLES[entry.status];
              return (
                <li key={`${entry.at}-${index}`} className="flex items-start gap-2 py-0.5">
                  <span
                    className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${style.dot}`}
                    title={style.label}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 break-words text-slate-300">
                    {ACTION_LABELS[entry.action.type] ?? entry.action.type}
                    {entry.detail ? ` — ${entry.detail}` : ''}
                  </span>
                  <span className="shrink-0 text-slate-500">{formatTime(entry.at)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Input */}
      <div className="flex shrink-0 items-center gap-2 border-t border-slate-700 bg-slate-800 p-2">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={onFileChange}
          tabIndex={-1}
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={!canAttach}
          aria-label="Joindre un fichier"
          title={
            backendAvailable
              ? 'Joindre un fichier à analyser'
              : 'Import de fichier indisponible — backend copilot hors ligne'
          }
          className="shrink-0 rounded-md border border-slate-600 p-1.5 text-slate-300 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="h-4 w-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"
            />
          </svg>
        </button>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={mode === 'agent' ? 'Demandez une action…' : 'Posez une question…'}
          aria-label="Message pour le copilot"
          className="min-w-0 flex-1 rounded-md border border-slate-600 bg-slate-900 px-2.5 py-1.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={sending || draft.trim() === ''}
          aria-label="Envoyer le message"
          className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Envoyer
        </button>
      </div>
    </div>
  );

  const toastStack = toasts.length > 0 && (
    <div className="fixed bottom-24 right-5 z-[10002] flex w-72 flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className="flex items-start gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 shadow-lg"
        >
          <span className="min-w-0 flex-1 break-words">{toast.message}</span>
          <button
            type="button"
            onClick={() => dismissToast(toast.id)}
            aria-label="Fermer la notification"
            className="shrink-0 text-slate-400 hover:text-slate-200"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      ))}
    </div>
  );

  return createPortal(
    <>
      {showWindow ? panel : bubble}
      {toastStack}
    </>,
    document.body,
  );
}
